import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, KpiCard, SectionTitle } from "@/components/Kpi";
import { COLORS, ComparisonChart } from "@/components/Charts";
import { api } from "@/lib/api";
import { num } from "@/lib/format";

const REDD = [["totale", "Redditività totale"], ["non_garantito", "Redditività non garantito"],
  ["garantito", "Redditività garantito"]];

export default function TassiPremi() {
  const { slug } = useParams();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState("");

  useEffect(() => {
    setDati(null);
    setErrore("");
    api.get("/corto/tassi-premi", { params: slug ? { slug } : {} })
      .then((r) => setDati(r.data))
      .catch(() => setErrore("Dati non disponibili"));
  }, [slug]);

  if (errore) return <Card className="p-8 text-sm text-slate-500" data-testid="tassi-errore">{errore}</Card>;
  if (!dati) return <div className="text-sm text-slate-500" data-testid="tassi-loading">Caricamento…</div>;

  const t = dati.totali;
  const redd = dati.redditivita;

  return (
    <div className="space-y-8" data-testid="tassi-premi-page">
      <SectionTitle sub={`Prestiti personali · ${dati.anno} · su pratiche liquidate`}
        right={<div className="flex items-center gap-3">
          <a href={`/api/corto/report/tassi-premi.pdf?anno=${dati.anno}${slug ? `&slug=${slug}` : ""}`}
            data-testid="btn-pdf-corto-tassi"
            className="px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm
              hover:bg-[#001480] transition-colors duration-200">Scarica PDF</a>
          {slug ? (
            <Link to={`/canale-corto/agenzie/${slug}`} data-testid="btn-torna-agenzia"
              className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500
                hover:text-[#C8102E] transition-colors duration-200">Torna alla scheda agenzia</Link>
          ) : (
            <Link to="/canale-corto" data-testid="btn-torna-dashboard-corto"
              className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500
                hover:text-[#C8102E] transition-colors duration-200">Torna alla dashboard</Link>
          )}
        </div>}>
        {dati.agenzia ? `${dati.agenzia} · Tassi, premi e redditività` : "Tassi, premi e redditività"}
      </SectionTitle>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Tasso medio ponderato non garantito" value={t.tasso_non_garantito}
          kind="quota" accent="caricato" testId="tassi-kpi-tasso-ng" />
        <KpiCard label="Tasso medio ponderato garantito" value={t.tasso_garantito}
          kind="quota" accent="liquidato" testId="tassi-kpi-tasso-g" />
        <KpiCard label="Redditività totale" value={redd?.totale?.totale} kind="quota"
          accent="slate" testId="tassi-kpi-redd-totale" />
        <KpiCard label="Redditività non garantito" value={redd?.non_garantito?.totale}
          kind="quota" accent="slate" testId="tassi-kpi-redd-ng" />
        <KpiCard label="Redditività garantito" value={redd?.garantito?.totale} kind="quota"
          accent="slate" testId="tassi-kpi-redd-g" />
      </div>

      <ComparisonChart testId="tassi-chart-tasso-ng" type="line" height={320} labelFormat="perc"
        title="Tasso medio per mese · non garantito" data={dati.tassi}
        keys={[{ key: "non_garantito", name: "Non garantito", color: "#7C3AED" }]} />

      <ComparisonChart testId="tassi-chart-tasso-g" type="line" height={320} labelFormat="perc"
        title="Tasso medio per mese · garantito" data={dati.tassi}
        keys={[{ key: "garantito", name: "Garantito", color: "#C084FC" }]} />

      <ComparisonChart testId="tassi-chart-penetrazione" height={340} labelFormat="perc" legenda
        title="Penetrazione assicurativa per mese" data={dati.penetrazione}
        keys={[{ key: "cpi", name: "CPI", color: COLORS.curr },
          { key: "bundle", name: "Bundle", color: COLORS.grigio },
          { key: "cpi_bundle", name: "CPI + Bundle", color: COLORS.pos }]} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="tassi-kpi-penetrazione">
        <KpiCard label="CPI" value={t.cpi_perc} kind="quota" accent="skyLight"
          sub="su pratiche liquidate" testId="tassi-kpi-cpi" />
        <KpiCard label="Bundle" value={t.bundle_perc} kind="quota" accent="skyLight"
          sub="su pratiche liquidate" testId="tassi-kpi-bundle" />
        <KpiCard label="CPI + Bundle" value={t.cpi_bundle_perc} kind="quota" accent="sky"
          sub="almeno una delle due" testId="tassi-kpi-cpi-bundle" />
      </div>

      {redd ? REDD.map(([tipo, titolo]) => (redd[tipo] ? (
        <ComparisonChart key={tipo} type="line" height={340} testId={`tassi-redd-${tipo}`}
          title={`${titolo} · ${num(redd[tipo].totale, 2)}%`}
          data={redd[tipo].serie} labelFormat="perc"
          keys={dati.agenzia
            ? [{ key: "agenzia", name: dati.agenzia, color: COLORS.caricato },
              { key: "media", name: "Media DBE", color: COLORS.grigio }]
            : [{ key: "media", name: "Media DBE", color: COLORS.curr }]} />
      ) : null)) : null}
    </div>
  );
}
